/**
 * Admin V3 — Billing / Invoices — Search / filter bar
 *
 * Layout (left → right):
 *   [Status filter] [Partner ID input] [Search input] [Result counter]
 *
 * Status filter: 6 InvoiceStatus values, server-side via ?status=
 * Partner ID input: server-side via ?partner_id=
 * Search input: client-side over loaded page (invoice_number, partner_name)
 *
 * NO CSV (no backend endpoint).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { invoiceStatusMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

const STATUS_VALUES = ['draft', 'issued', 'paid', 'partially_paid', 'overdue', 'voided'];

const BillingInvoicesSearchBar = ({
  status,
  onStatusChange,
  partnerId,
  onPartnerIdChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const selectValue = status || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('billing.invoices.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status filter */}
      <Select value={selectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[200px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-billing-invoices-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('billing.invoices.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{invoiceStatusMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Partner ID input (server-side) */}
      <input
        type="text"
        value={partnerId}
        onChange={(e) => onPartnerIdChange(e.target.value)}
        placeholder={t('billing.invoices.partner.placeholder', locale)}
        data-testid="adminv3-billing-invoices-partner-input"
        className="h-10 w-[260px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
      />

      {/* Client-side search */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('billing.invoices.search.placeholder', locale)}
          data-testid="adminv3-billing-invoices-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-billing-invoices-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-billing-invoices-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default BillingInvoicesSearchBar;
